import { nextTick, ref, type Ref } from "vue";
import { getShopList } from "@/common/graphql/shop";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import type { ShopListInput } from "@/generated/graphql";
import type { ChainArray, ShopArray, ListData, ListOptions } from "../types";

/**
 * 画面で共有する状態を初期化して返却する composable。
 *
 * @returns 画面で利用するリアクティブな状態オブジェクト
 */
export const useDeliveryMoveFilterState = () => {
  // チェーンリスト
  const chainListItems = ref<ChainArray>([]);
  const bufChainListItems = ref<ChainArray>([]);
  const chainSelectValues = ref<{ chain_code: string; name: string }[]>([]);
  const chainAllCheckFlag = ref<string>("all");
  const searchConditionChainCode = ref<string[]>([]);

  // 店舗
  const shopListItems = ref<ShopArray>([]);
  const bufShopListItems = ref<ShopArray>([]);

  const shopSelectValuesFrom = ref<{ shop_code: string; name: string }[]>([]);
  const shopSelectValuesTo = ref<{ shop_code: string; name: string }[]>([]);
  const searchShopSelectValuesFrom = ref<string[]>([]);
  const searchShopSelectValuesTo = ref<string[]>([]);
  const shopFromAllCheckFlag = ref<string>("none");
  const shopToAllCheckFlag = ref<string>("none");

  // 店舗名をdisableにするかのフラグ
  const shopDisabledFlag = ref(true);

  return {
    chainListItems,
    bufChainListItems,
    chainSelectValues,
    chainAllCheckFlag,
    searchConditionChainCode,
    shopListItems,
    bufShopListItems,
    shopSelectValuesFrom,
    shopSelectValuesTo,
    searchShopSelectValuesFrom,
    searchShopSelectValuesTo,
    shopFromAllCheckFlag,
    shopToAllCheckFlag,
    shopDisabledFlag,
  };
};

interface UseDeliveryMoveFilterOptionsParams {
  chainListItems: Ref<ChainArray>;
  bufChainListItems: Ref<ChainArray>;
  chainSelectValues: Ref<{ chain_code: string; name: string }[]>;
  chainAllCheckFlag: Ref<string>;
  searchConditionChainCode: Ref<string[]>;
  shopListItems: Ref<ShopArray>;
  bufShopListItems: Ref<ShopArray>;
  shopSelectValuesFrom: Ref<{ shop_code: string; name: string }[]>;
  shopSelectValuesTo: Ref<{ shop_code: string; name: string }[]>;
  searchShopSelectValuesFrom: Ref<string[]>;
  searchShopSelectValuesTo: Ref<string[]>;
  shopFromAllCheckFlag: Ref<string>;
  shopToAllCheckFlag: Ref<string>;
  shopDisabledFlag: Ref<boolean>;
  shopListInput: ShopListInput;
  listDataArray: Ref<ListData[]>;
  listOptions: Ref<ListOptions>;
  listDataTotalCount: Ref<number>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
}

/**
 * 検索・絞り込みで使用する選択肢データを提供する composable。
 *
 * @returns 画面フォームで利用する状態と操作関数
 */
export const useDeliveryMoveFilterOptions = ({
  chainListItems,
  bufChainListItems,
  chainSelectValues,
  chainAllCheckFlag,
  searchConditionChainCode,
  shopListItems,
  bufShopListItems,
  shopSelectValuesFrom,
  shopSelectValuesTo,
  searchShopSelectValuesFrom,
  searchShopSelectValuesTo,
  shopFromAllCheckFlag,
  shopToAllCheckFlag,
  shopDisabledFlag,
  shopListInput,
  listDataArray,
  listOptions,
  listDataTotalCount,
  errdialog,
  errMessage,
}: UseDeliveryMoveFilterOptionsParams) => {
  /**
   * 「チェーン」全選択 @change
   */
  const onchangeChainAllCheck = async (): Promise<void> => {
    if (chainAllCheckFlag.value === "all" || chainAllCheckFlag.value === "some") {
      // チェックが外された
      chainSelectValues.value = [];
      searchConditionChainCode.value = [];

      shopSelectValuesFrom.value = [];
      shopSelectValuesTo.value = [];
      shopListItems.value = [];
      listDataArray.value = [];
      searchShopSelectValuesFrom.value = [];
      searchShopSelectValuesTo.value = [];

      chainAllCheckFlag.value = "none";
      shopFromAllCheckFlag.value = "none";
      shopToAllCheckFlag.value = "none";
      shopDisabledFlag.value = true;
    } else {
      // 全チェック
      chainAllCheckFlag.value = "all";
      bufChainListItems.value?.forEach((chain_value) => {
        chainSelectValues.value.push({
          chain_code: chain_value!.chain_code,
          name: chain_value!.name,
        });
        searchConditionChainCode.value.push(chain_value!.chain_code);
      });

      // 複数チェーン選択時は店舗を無効化
      shopSelectValuesFrom.value = [];
      shopSelectValuesTo.value = [];
      shopListItems.value = [];
      searchShopSelectValuesFrom.value = [];
      searchShopSelectValuesTo.value = [];
      shopFromAllCheckFlag.value = "none";
      shopToAllCheckFlag.value = "none";
      shopDisabledFlag.value = true;
    }
    await nextTick(() => {
      console.log(chainAllCheckFlag.value); // log is 'updated.'
    });
  };

  /**
   * 「チェーン」選択ボックス @change
   */
  const onChangeChain = async (): Promise<void> => {
    try {
      shopSelectValuesFrom.value = [];
      shopSelectValuesTo.value = [];
      shopListItems.value = [];
      listDataArray.value = [];
      searchShopSelectValuesTo.value = [];

      // ページを1ページに戻す
      listOptions.value.page = 1;
      listDataTotalCount.value = 0;

      // チェーン名が空の場合、店舗、エリア名も空にする
      if (!chainSelectValues.value) {
        return;
      }

      // 初期化
      searchConditionChainCode.value = [];
      chainAllCheckFlag.value = "none";

      // 全選択状態かどうか
      if (chainSelectValues.value.length === chainListItems.value!.length) {
        chainAllCheckFlag.value = "all";
      } else if (chainSelectValues.value.length > 0) {
        chainAllCheckFlag.value = "some";
      } else {
        chainAllCheckFlag.value = "none";
        shopFromAllCheckFlag.value = "none";
        shopToAllCheckFlag.value = "none";
      }

      await nextTick(() => {
        console.log(chainAllCheckFlag.value); // log is 'updated.'
      });

      chainListItems.value!.forEach((chain_value) => {
        chainSelectValues.value.forEach((chain_select_value) => {
          if (chain_value!.chain_code === chain_select_value!.chain_code) {
            searchConditionChainCode.value.push(chain_value!.chain_code);
          }
        });
      });

      // 店舗情報を取得する
      if (chainSelectValues.value.length === 1) {
        shopListInput.chain_code = chainSelectValues.value[0].chain_code;
        bufShopListItems.value = await getShopList(shopListInput);

        if (bufShopListItems.value && bufShopListItems.value.length > 0) {
          bufShopListItems.value.forEach((shop_value) => {
            shopListItems.value!.push({
              shop_code: shop_value!.shop_code,
              name: shop_value!.name,
            });
            shopSelectValuesFrom.value.push({
              shop_code: shop_value!.shop_code,
              name: shop_value!.name,
            });
            searchShopSelectValuesFrom.value.push(shop_value!.shop_code);
            shopFromAllCheckFlag.value = "all";

            shopSelectValuesTo.value.push({
              shop_code: shop_value!.shop_code,
              name: shop_value!.name,
            });
            searchShopSelectValuesTo.value.push(shop_value!.shop_code);
            shopToAllCheckFlag.value = "all";
          });

          // チェーンをすぐに変更されたときの処理
          if (chainSelectValues.value.length !== 1) {
            shopSelectValuesFrom.value = [];
            shopSelectValuesTo.value = [];
            shopListItems.value = [];
            listDataArray.value = [];
            searchShopSelectValuesFrom.value = [];
            searchShopSelectValuesTo.value = [];
            shopDisabledFlag.value = true;
            shopFromAllCheckFlag.value = "none";
            shopToAllCheckFlag.value = "none";
          } else {
            shopFromAllCheckFlag.value = "all";
            shopToAllCheckFlag.value = "all";
            shopDisabledFlag.value = false;
          }
        }
      } else {
        shopFromAllCheckFlag.value = "none";
        shopToAllCheckFlag.value = "none";
        shopDisabledFlag.value = true;
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = err.message;
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "不明なエラーが発生しました。";
      }
      errdialog.value = true;
      return;
    }
  };

  /**
   * 「店舗」全選択 @change
   */
  const onChangeShopAllCheck = async (shopType: number): Promise<void> => {
    if (shopDisabledFlag.value) {
      return;
    }
    if (shopType === 0) {
      if (shopFromAllCheckFlag.value === "all" || shopFromAllCheckFlag.value === "some") {
        // チェックが外された
        shopSelectValuesFrom.value = [];
        searchShopSelectValuesFrom.value = [];
        shopFromAllCheckFlag.value = "none";
      } else {
        // 全チェック
        shopFromAllCheckFlag.value = "all";
        bufShopListItems.value!.forEach((shop_value) => {
          shopSelectValuesFrom.value.push({
            shop_code: shop_value!.shop_code,
            name: shop_value!.name,
          });
          searchShopSelectValuesFrom.value.push(shop_value!.shop_code);
        });
      }
      await nextTick();
      console.log(shopFromAllCheckFlag.value);
    } else {
      if (shopToAllCheckFlag.value === "all" || shopToAllCheckFlag.value === "some") {
        // チェックが外された
        shopSelectValuesTo.value = [];
        searchShopSelectValuesTo.value = [];
        shopToAllCheckFlag.value = "none";
      } else {
        // 全チェック
        shopToAllCheckFlag.value = "all";
        bufShopListItems.value!.forEach((shop_value) => {
          shopSelectValuesTo.value.push({
            shop_code: shop_value!.shop_code,
            name: shop_value!.name,
          });
          searchShopSelectValuesTo.value.push(shop_value!.shop_code);
        });
      }
      await nextTick();
      console.log(shopToAllCheckFlag.value);
    }
  };

  /**
   * 「店舗」選択ボックス変更 @change
   */
  const onChangeShop = async (shopType: number): Promise<void> => {
    try {
      // ページを1に戻す
      listOptions.value.page = 1;
      listDataTotalCount.value = 0;

      if (shopType === 0) {
        // 一時バッファ(店舗コード)
        const buf = [...shopSelectValuesFrom.value];

        //一旦全部からにする
        shopSelectValuesFrom.value = [];
        searchShopSelectValuesFrom.value = [];
        shopFromAllCheckFlag.value = "none";

        // 全選択状態か判定
        if (buf.length === bufShopListItems.value!.length) {
          shopFromAllCheckFlag.value = "all";
        } else if (buf.length > 0) {
          shopFromAllCheckFlag.value = "some";
        }

        await nextTick();
        console.log(shopFromAllCheckFlag.value);

        bufShopListItems.value!.forEach((shop_value) => {
          buf.forEach((shop_select_value) => {
            if (shop_value!.shop_code === shop_select_value.shop_code) {
              shopSelectValuesFrom.value.push({
                shop_code: shop_value!.shop_code,
                name: shop_value!.name,
              });
              searchShopSelectValuesFrom.value.push(shop_value!.shop_code);
            }
          });
        });
      } else {
        // to 側
        const buf = [...shopSelectValuesTo.value];
        shopSelectValuesTo.value = [];
        searchShopSelectValuesTo.value = [];
        shopToAllCheckFlag.value = "none";

        // 全選択状態か判定
        if (buf.length === bufShopListItems.value!.length) {
          shopToAllCheckFlag.value = "all";
        } else if (buf.length > 0) {
          shopToAllCheckFlag.value = "some";
        }

        await nextTick();
        console.log(shopToAllCheckFlag.value);

        bufShopListItems.value!.forEach((shop_value) => {
          buf.forEach((shop_select_value) => {
            if (shop_value!.shop_code === shop_select_value.shop_code) {
              shopSelectValuesTo.value.push({
                shop_code: shop_value!.shop_code,
                name: shop_value!.name,
              });
              searchShopSelectValuesTo.value.push(shop_value!.shop_code);
            }
          });
        });
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
    }
  };

  return {
    onchangeChainAllCheck,
    onChangeChain,
    onChangeShopAllCheck,
    onChangeShop,
  };
};
